import React, { useState, useEffect, useContext } from 'react';
import '../styles/MealPlanner.css';
import { Helmet } from 'react-helmet';
import { auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { trackMealGeneration, trackMealSaved, trackButtonClick, trackNutritionAnalysis } from '../utils/analytics';
import { fetchRecipe, Meal, Recipe, RecipeCacheState } from '../utils/recipeUtils';

interface SelectionChip {
  id: string;
  name: string;
}

interface NutritionTotals {
  totalCalories: number;
  totalProtein: number;
  totalCarbs: number;
  totalFat: number;
}

interface RecipeModalProps {
  isOpen: boolean;
  recipe: Recipe | null;
  loading: boolean;
  onClose: () => void;
}

const RecipeModal: React.FC<RecipeModalProps> = ({ isOpen, recipe, loading, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="recipe-modal-overlay">
      <div className="recipe-modal">
        <button className="close-modal-btn" onClick={onClose}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        
        {loading ? (
          <div className="recipe-loading">
            <div className="loading-spinner"></div>
            <p>Fetching your recipe...</p>
          </div>
        ) : recipe ? (
          <div className="recipe-content">
            <h2 className="recipe-title">{recipe.title}</h2>
            <p className="recipe-description">{recipe.description}</p>
            
            <div className="recipe-meta">
              <div className="recipe-meta-item">
                <span className="meta-label">Prep Time:</span>
                <span className="meta-value">{recipe.prepTime}</span>
              </div>
              <div className="recipe-meta-item">
                <span className="meta-label">Cook Time:</span>
                <span className="meta-value">{recipe.cookTime}</span>
              </div>
              <div className="recipe-meta-item">
                <span className="meta-label">Total Time:</span>
                <span className="meta-value">{recipe.totalTime}</span>
              </div>
              <div className="recipe-meta-item">
                <span className="meta-label">Servings:</span>
                <span className="meta-value">{recipe.servings}</span>
              </div>
            </div>
            
            <div className="recipe-section">
              <h3>Ingredients</h3>
              <ul className="recipe-ingredients" style={{ listStyleType: 'none' }}>
                {recipe.ingredients.map((ingredient, index) => {
                  // Remove any leading bullet characters or dots
                  const cleanIngredient = ingredient.replace(/^[•⦁·⚫⚪◦◉◎▪▫◆◇■□●○]?\s*/, '');
                  return <li key={index}>{cleanIngredient}</li>;
                })}
              </ul>
            </div>
            
            <div className="recipe-section">
              <h3>Instructions</h3>
              <ol className="recipe-instructions" style={{ listStyleType: 'none', paddingLeft: 0 }}>
                {recipe.instructions.map((instruction, index) => {
                  // Remove any leading numbers if present
                  const cleanInstruction = instruction.replace(/^\d+\.\s*/, '');
                  return <li key={index}>{cleanInstruction}</li>;
                })}
              </ol>
            </div>
            
            <div className="recipe-section">
              <h3>Nutrition Information</h3>
              <div className="recipe-nutrition">
                <div className="nutrition-item">
                  <span className="nutrition-value">{recipe.nutritionInfo.calories}</span>
                  <span className="nutrition-label">CALORIES</span>
                </div>
                <div className="nutrition-item">
                  <span className="nutrition-value">{recipe.nutritionInfo.protein}g</span>
                  <span className="nutrition-label">PROTEIN</span>
                </div>
                <div className="nutrition-item">
                  <span className="nutrition-value">{recipe.nutritionInfo.carbs}g</span>
                  <span className="nutrition-label">CARBS</span>
                </div>
                <div className="nutrition-item">
                  <span className="nutrition-value">{recipe.nutritionInfo.fat}g</span>
                  <span className="nutrition-label">FAT</span>
                </div>
              </div>
            </div>
            
            {recipe.tips && recipe.tips.length > 0 && (
              <div className="recipe-section">
                <h3>Tips</h3>
                <ul className="recipe-tips" style={{ listStyleType: 'none' }}>
                  {recipe.tips.map((tip, index) => {
                    // Remove any leading bullet characters or dots
                    const cleanTip = tip.replace(/^[•⦁·⚫⚪◦◉◎▪▫◆◇■□●○]?\s*/, '');
                    return <li key={index}>{cleanTip}</li>;
                  })}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="recipe-error">
            <h3>Unable to generate recipe</h3>
            <p>We couldn't generate a recipe for this meal. Please try again later.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const dietaryPreferences: SelectionChip[] = [
  { id: 'gluten-free', name: 'Gluten Free' },
  { id: 'dairy-free', name: 'Dairy Free' },
  { id: 'vegetarian', name: 'Vegetarian' },
  { id: 'vegan', name: 'Vegan' },
  { id: 'keto', name: 'Keto' },
  { id: 'paleo', name: 'Paleo' },
  { id: 'low-carb', name: 'Low Carb' },
  { id: 'low-fat', name: 'Low Fat' },
  { id: 'high-protein', name: 'High Protein' }
];

// Common allergens list
const commonAllergens: SelectionChip[] = [
  { id: 'peanuts', name: 'Peanuts' },
  { id: 'tree-nuts', name: 'Tree Nuts' },
  { id: 'milk', name: 'Milk' },
  { id: 'eggs', name: 'Eggs' },
  { id: 'fish', name: 'Fish' },
  { id: 'shellfish', name: 'Shellfish' },
  { id: 'soy', name: 'Soy' },
  { id: 'wheat', name: 'Wheat' },
  { id: 'sesame', name: 'Sesame' },
  { id: 'gluten', name: 'Gluten' },
  { id: 'dairy', name: 'Dairy' }
];

// Health goals options
const healthGoals: SelectionChip[] = [
  { id: 'maintain', name: 'Maintain Weight' },
  { id: 'lose-weight', name: 'Lose Weight' },
  { id: 'gain-weight', name: 'Gain Weight' },
  { id: 'gain-muscle', name: 'Gain Muscle' },
  { id: 'improve-energy', name: 'Improve Energy' },
  { id: 'heart-health', name: 'Heart Health' }
];

const MealPlanner: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dietType, setDietType] = useState<string>('balanced');
  const [selectedAllergens, setSelectedAllergens] = useState<string[]>([]);
  const [selectedRestrictions, setSelectedRestrictions] = useState<string[]>([]);
  const [selectedGoal, setSelectedGoal] = useState<string>('maintain');
  const [calorieTarget, setCalorieTarget] = useState<number>(2000);
  const [userWeight, setUserWeight] = useState<number>(70);
  const [weightUnit, setWeightUnit] = useState<string>('kg');
  const [mealsPerDay, setMealsPerDay] = useState<number>(3);
  const [results, setResults] = useState<boolean>(false);
  const [generatedMeals, setGeneratedMeals] = useState<Meal[]>([]);
  const [nutritionTotals, setNutritionTotals] = useState<NutritionTotals>({
    totalCalories: 0,
    totalProtein: 0,
    totalCarbs: 0,
    totalFat: 0
  });
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [savingMeal, setSavingMeal] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  
  // Add recipe-related state
  const [isRecipeModalOpen, setIsRecipeModalOpen] = useState<boolean>(false);
  const [recipeLoading, setRecipeLoading] = useState<boolean>(false);
  const [currentRecipe, setCurrentRecipe] = useState<Recipe | null>(null);
  const [savedRecipes, setSavedRecipes] = useState<{[key: string]: Recipe}>({});

  // Add this new useEffect for page title and metadata
  useEffect(() => {
    // Update document title for better SEO
    document.title = "AI Meal Planner | personalised Nutrition Made Easy";
  }, []);

  // Add authentication listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    
    return () => unsubscribe();
  }, []);

  // Toggle dietary restriction selection
  const toggleRestriction = (id: string) => {
    setSelectedRestrictions(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id) 
        : [...prev, id]
    );
  };

  // Toggle allergen selection
  const toggleAllergen = (id: string) => {
    setSelectedAllergens(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id) 
        : [...prev, id]
    );
  };

  // Select health goal
  const selectGoal = (id: string) => {
    setSelectedGoal(id);
    // Update calorie target when health goal changes
    calculateCalorieTarget(userWeight, weightUnit, id);
  };

  // Calculate calorie target based on weight and health goal
  const calculateCalorieTarget = (weight: number, unit: string, goal: string) => {
    // Convert weight to kg if in lbs
    const weightInKg = unit === 'lbs' ? weight * 0.453592 : weight;
    
    // Base calculation (rough estimate)
    let calories = weightInKg * 30; // ~30 calories per kg for maintenance
    
    // Adjust based on health goal
    switch(goal) {
      case 'lose-weight':
        calories = Math.round(calories * 0.8); // 20% deficit
        break;
      case 'gain-weight':
        calories = Math.round(calories * 1.15); // 15% surplus
        break;
      case 'gain-muscle':
        calories = Math.round(calories * 1.1); // 10% surplus
        break;
      default:
        // maintain and other goals use the base calculation
        calories = Math.round(calories);
    }
    
    setCalorieTarget(calories);
  };

  // Handle weight change
  const handleWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newWeight = parseFloat(e.target.value);
    if (!isNaN(newWeight) && newWeight > 0) {
      setUserWeight(newWeight);
      calculateCalorieTarget(newWeight, weightUnit, selectedGoal);
    }
  };

  // Handle weight unit change
  const handleWeightUnitChange = (unit: string) => {
    if (unit === weightUnit) return; // No change needed
    
    // Convert the weight value when changing units
    const newWeight = unit === 'kg' 
      ? Math.round(userWeight * 0.453592) // Convert from lbs to kg
      : Math.round(userWeight / 0.453592); // Convert from kg to lbs
    
    setUserWeight(newWeight);
    calculateCalorieTarget(newWeight, unit, selectedGoal);
    setWeightUnit(unit);
  };

  // Handle step navigation
  const goToNextStep = async () => {
    window.scrollTo(0, 0);
    if (currentStep === 2) {
      setIsLoading(true);
      try {
        // Create a list of dietary preferences selected
        const preferences = selectedRestrictions.map(id => {
          const pref = dietaryPreferences.find(p => p.id === id);
          return pref ? pref.name : id;
        });
        
        // Create a list of allergens to avoid
        const allergensToAvoid = selectedAllergens.map(id => {
          const allergen = commonAllergens.find(a => a.id === id);
          return allergen ? allergen.name : id;
        });
        
        const apiEndpoint = 'https://ioentb1rc5.execute-api.eu-north-1.amazonaws.com/prod/generatemealplan';
        
        const response = await fetch(apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            dietType,
            calorieTarget,
            mealsPerDay,
            selectedRestrictions,
            allergies: selectedAllergens
          }),
        });
        
        if (!response.ok) {
          throw new Error('Failed to generate meal plan');
        }
        
        const data = await response.json();
        
        // Update state with the meal plan data
        setGeneratedMeals(data.mealPlan.meals);
        
        // If the app has snacks functionality, uncomment the line below
        // setGeneratedSnacks(data.mealPlan.snacks);
        
        setNutritionTotals(data.nutrition);
        setIsLoading(false);
        setResults(true);
        
        // Track meal plan generation
        trackMealGeneration(
          [...preferences, ...allergensToAvoid, selectedGoal],
          data.mealPlan?.meals?.length || 0
        );
        
        setCurrentStep(3);
      } catch (error) {
        console.error('Error generating meal plan:', error);
        setIsLoading(false);
        // Show error to user
        alert('Failed to generate meal plan. Please try again.');
      }
    } else {
      setCurrentStep(prevStep => prevStep + 1);
    }
  };

  const goToPreviousStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  // Create a new meal plan
  const createNewPlan = () => {
    // Reset form state
    setSelectedRestrictions([]);
    setSelectedAllergens([]);
    setSelectedGoal('maintain');
    setCalorieTarget(2000);
    setUserWeight(70);
    setWeightUnit('kg');
    setMealsPerDay(3);
    setResults(false);
    setGeneratedMeals([]);
    setNutritionTotals({
      totalCalories: 0,
      totalProtein: 0,
      totalCarbs: 0,
      totalFat: 0
    });
    setCurrentStep(1);
    // Track button click for creating new plan
    trackButtonClick('create_new_plan', 'meal_planner');
  };

  // Save meal to user's profile
  const saveMeal = async (meal: Meal) => {
    // Clear previous states
    setSaveSuccess(null);
    setSaveError(null);
    
    let userId = currentUser?.uid;
    
    // If no user is logged in, generate a temporary user ID and store in local storage
    if (!userId) {
      userId = localStorage.getItem('tempUserId');
      if (!userId) {
        userId = `anon_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
        localStorage.setItem('tempUserId', userId);
      }
    }

    try {
      // Fix the linter errors by checking if meal.id exists first
      if (meal.id) {
        setSavingMeal(meal.id);
      }
      
      // Store the original meal ID before saving
      const originalMealId = meal.id || meal.mealId;
      
      const apiEndpoint = `${process.env.REACT_APP_API_GATEWAY_URL}/prod/savemeal`;
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          meal: {
            title: meal.title || meal.name,
            calories: meal.calories,
            protein: meal.protein,
            carbs: meal.carbs,
            fat: meal.fat,
            ingredients: meal.ingredients || [],
            timeToPrep: meal.timeToPrep || '15-20 minutes',
            originalMealId: originalMealId // Add the original meal ID
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save meal');
      }

      const data = await response.json();
      
      // If we got a new mealId back, create a mapping to the original mealId
      if (data.mealId && originalMealId) {
        // Import the function from recipeUtils
        const saveMealIdMapping = (origId: string, newId: string) => {
          try {
            const mappings = localStorage.getItem('aiNutrition_mealIdMappings');
            let idMap: Record<string, string> = {};
            
            if (mappings) {
              idMap = JSON.parse(mappings) as Record<string, string>;
            }
            
            idMap[newId] = origId;
            localStorage.setItem('aiNutrition_mealIdMappings', JSON.stringify(idMap));
          } catch (error) {
            console.error('Error saving meal ID mapping:', error);
          }
        };
        
        saveMealIdMapping(originalMealId, data.mealId);
      }
      
      // Fix the linter errors by checking if meal.id exists first
      if (meal.id) {
        setSaveSuccess(meal.id);
      }
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving meal:', error);
      setSaveError('Failed to save meal');
      setTimeout(() => setSaveError(null), 3000);
    } finally {
      setSavingMeal(null);
    }
  };

  // Add handleViewRecipe function
  const handleViewRecipe = async (meal: Meal) => {
    setIsRecipeModalOpen(true);
    
    // Track recipe view from meal planner
    trackButtonClick('view_recipe', 'meal_planner');
    
    await fetchRecipe(
      meal,
      savedRecipes,
      setSavedRecipes,
      setCurrentRecipe,
      setRecipeLoading
    );
  };

  // Add closeRecipeModal function
  const closeRecipeModal = () => {
    setIsRecipeModalOpen(false);
  };

  // Render dietary preferences step
  const renderPreferencesStep = () => {
    return (
      <div className="preferences-section">
        <h2>Dietary Preferences</h2>
        <div className="preferences-grid">
          <div className="health-goals-container">
            <h3>Health Goals</h3>
            <div className="goals-chips">
              {healthGoals.map(goal => (
                <div 
                  key={goal.id}
                  className={`goal-chip ${selectedGoal === goal.id ? 'active' : ''}`}
                  onClick={() => selectGoal(goal.id)}
                >
                  {goal.name}
                </div>
              ))}
            </div>
          </div>
          
          <div className="allergies-container">
            <h3>Allergies and Intolerances</h3>
            <div className="allergies-chips">
              {commonAllergens.map(allergen => (
                <div 
                  key={allergen.id}
                  className={`allergen-chip ${selectedAllergens.includes(allergen.id) ? 'active' : ''}`}
                  onClick={() => toggleAllergen(allergen.id)}
                >
                  {allergen.name}
                </div>
              ))}
            </div>
          </div>
          
          <div className="restrictions-container">
            <h3>Dietary Restrictions</h3>
            <div className="restrictions-chips">
              {dietaryPreferences.map(preference => (
                <div 
                  key={preference.id}
                  className={`restriction-chip ${selectedRestrictions.includes(preference.id) ? 'active' : ''}`}
                  onClick={() => toggleRestriction(preference.id)}
                >
                  {preference.name}
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className="step-buttons">
          <div></div> {/* Empty div for space-between alignment */}
          <button 
            onClick={goToNextStep} 
            className="button button-primary"
          >
            Next
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"></line>
              <polyline points="12 5 19 12 12 19"></polyline>
            </svg>
          </button>
        </div>
      </div>
    );
  };

  // Render nutritional goals step
  const renderGoalsStep = () => {
    return (
      <div className="goals-section">
        <h2>Nutritional Goals</h2>
        
        <div className="weight-input-container">
          <h3>Your Weight</h3>
          <div className="weight-input-wrapper">
            <input
              type="number"
              min="20"
              max="250"
              value={userWeight}
              onChange={handleWeightChange}
              className="weight-input"
            />
            <div className="weight-unit-toggle">
              <button 
                className={`unit-button ${weightUnit === 'kg' ? 'active' : ''}`}
                onClick={() => handleWeightUnitChange('kg')}
              >
                kg
              </button>
              <button 
                className={`unit-button ${weightUnit === 'lbs' ? 'active' : ''}`}
                onClick={() => handleWeightUnitChange('lbs')}
              >
                lbs
              </button>
            </div>
          </div>
          <p className="calorie-estimate">Estimated daily calories: <strong>{calorieTarget}</strong></p>
          <p className="calorie-info">Based on your weight and goal: <strong>{healthGoals.find(g => g.id === selectedGoal)?.name}</strong></p>
        </div>
        
        <div className="meals-per-day">
          <h3>Meals Per Day</h3>
          <div className="meals-buttons">
            <button 
              className={`meal-button ${mealsPerDay === 2 ? 'active' : ''}`}
              onClick={() => setMealsPerDay(2)}
            >
              2
            </button>
            <button 
              className={`meal-button ${mealsPerDay === 3 ? 'active' : ''}`}
              onClick={() => setMealsPerDay(3)}
            >
              3
            </button>
            <button 
              className={`meal-button ${mealsPerDay === 4 ? 'active' : ''}`}
              onClick={() => setMealsPerDay(4)}
            >
              4
            </button>
            <button 
              className={`meal-button ${mealsPerDay === 5 ? 'active' : ''}`}
              onClick={() => setMealsPerDay(5)}
            >
              5
            </button>
          </div>
        </div>
        
        <div className="step-buttons">
          <button 
            onClick={goToPreviousStep}
            className="button button-secondary"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            Back
          </button>
          <button 
            onClick={goToNextStep}
            className="button button-primary"
          >
            Generate Meal Plan
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
            </svg>
          </button>
        </div>
      </div>
    );
  };

  // Render meal card
  const renderMealCard = (meal: any) => {
    // Transform API meal data format to frontend format if needed
    // The API returns meals with 'name' but frontend expects 'title'
    // And we need to handle missing properties like ingredients and timeToPrep
    
    return (
      <article className="meal-card" key={meal.id || meal.name}>
        <div className="meal-content">
          <h4 className="meal-title">
            {meal.title || meal.name}
          </h4>
          
          <div className="meal-stats">
            <div className="meal-stat">
              <div className="meal-stat-value">{meal.calories}</div>
              <div className="meal-stat-label">Calories</div>
            </div>
            <div className="meal-stat">
              <div className="meal-stat-value">{meal.protein}g</div>
              <div className="meal-stat-label">Protein</div>
            </div>
            <div className="meal-stat">
              <div className="meal-stat-value">{meal.carbs}g</div>
              <div className="meal-stat-label">Carbs</div>
            </div>
            <div className="meal-stat">
              <div className="meal-stat-value">{meal.fat}g</div>
              <div className="meal-stat-label">Fat</div>
            </div>
          </div>
          
          {meal.ingredients && (
            <div>
              <div className="ingredients-title">Ingredients:</div>
              <div className="ingredients-list">
                {meal.ingredients.map((ingredient: string, index: number) => (
                  <span key={index} className="ingredient-chip">{ingredient}</span>
                ))}
              </div>
            </div>
          )}
          
          {meal.timeToPrep && (
            <div className="meal-footer">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              {meal.timeToPrep}
            </div>
          )}
        </div>
        
        <div className="meal-actions">
          <button className="meal-action-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
            </svg>
            Customize
          </button>
          <button 
            className="meal-action-btn"
            onClick={() => handleViewRecipe(meal)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10 9 9 9 8 9"/>
            </svg>
            Recipe
          </button>
          <button 
            className={`meal-action-btn ${savingMeal === meal.id ? 'saving' : ''} ${saveSuccess === meal.id ? 'success' : ''}`}
            onClick={() => saveMeal(meal)}
            disabled={savingMeal === meal.id}
          >
            {savingMeal === meal.id ? (
              <span className="saving-indicator">Saving...</span>
            ) : saveSuccess === meal.id ? (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                Saved
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Save
              </>
            )}
          </button>
        </div>
        
        {saveError && (
          <div className="save-error-message">{saveError}</div>
        )}
      </article>
    );
  };

  // Render results step
  const renderResultsStep = () => {
    return (
      <article className="results-section">
        <div className="results-header">
          <h2>Your AI Meal Plan</h2>
          <div>
            <div className="nutrition-summary">
              <div className="nutrition-summary-item">
                <span className="summary-label">Total Calories:</span>
                <span className="summary-value">{nutritionTotals.totalCalories}</span>
              </div>
              <div className="nutrition-summary-item">
                <span className="summary-label">Protein:</span>
                <span className="summary-value">{nutritionTotals.totalProtein}g</span>
              </div>
              <div className="nutrition-summary-item">
                <span className="summary-label">Carbs:</span>
                <span className="summary-value">{nutritionTotals.totalCarbs}g</span>
              </div>
              <div className="nutrition-summary-item">
                <span className="summary-label">Fat:</span>
                <span className="summary-value">{nutritionTotals.totalFat}g</span>
              </div>
            </div>
            {/* <button className="button button-secondary">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Save Plan
            </button> */}
          </div>
        </div>
        
        <h3>AI-Generated Meals Just For You</h3>
        <p className="meal-plan-description">These meals are tailored to your specific dietary needs and preferences using our advanced AI Meal Planner technology.</p>
        <div className="meals-container">
          {generatedMeals.map(meal => renderMealCard(meal))}
        </div>
        
        <button 
          onClick={createNewPlan}
          className="button button-primary create-new-button"
        >
          Create New AI Meal Plan
        </button>
      </article>
    );
  };

  // Render loading state
  const renderLoading = () => {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <h3>Generating Your Custom Meal Plan</h3>
        <p>Our AI is crafting the perfect meals based on your preferences...</p>
      </div>
    );
  };

  return (
    <main className="meal-planner">
      {/* Add Helmet for additional per-page SEO metadata */}
      <Helmet>
        <title>AI Meal Planner | personalised Nutrition Made Easy</title>
        <meta name="description" content="Generate personalised meal plans with our AI Meal Planner based on your dietary preferences, allergies, and health goals." />
        <meta name="keywords" content="AI Meal Planner, meal planning, nutrition, diet plan, healthy eating, personalised meals" />
        <link rel="canonical" href={process.env.REACT_APP_SITE_URL + "/meal-planner"} />
      </Helmet>
      
      {/* Recipe Modal */}
      <RecipeModal 
        isOpen={isRecipeModalOpen}
        recipe={currentRecipe}
        loading={recipeLoading}
        onClose={closeRecipeModal}
      />
      
      <header className="meal-planner-header">
        <h1 className="meal-planner-title">AI Meal Planner</h1>
        <p className="meal-planner-subtitle">Get personalised meal plans based on your dietary preferences and nutritional goals</p>
      </header>
      
      {/* Step indicator */}
      <nav className="step-indicator" aria-label="Progress">
        <div className={`step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
          <div className="step-number">1</div>
          <div className="step-name">Dietary Preferences</div>
        </div>
        <div className={`step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
          <div className="step-number">2</div>
          <div className="step-name">Nutritional Goals</div>
        </div>
        <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
          <div className="step-number">3</div>
          <div className="step-name">Your AI Meal Plan</div>
        </div>
      </nav>
      
      {/* Step content */}
      <section aria-live="polite">
        {isLoading ? renderLoading() : (
          <>
            {currentStep === 1 && renderPreferencesStep()}
            {currentStep === 2 && renderGoalsStep()}
            {currentStep === 3 && results && renderResultsStep()}
          </>
        )}
      </section>
      
      <footer className="meal-planner-footer">
        <p>Powered by AI Meal Planner technology - Create your personalised meal plan today</p>
      </footer>
    </main>
  );
};

export default MealPlanner; 